import React from 'react';
import PropTypes from 'prop-types';
import Underscore from 'underscore';
import './index.scss';

export default class FastScrollbox extends React.Component {
  static propTypes = {
    buildChild: PropTypes.func.isRequired,
    childCount: PropTypes.number.isRequired,
    childHeight: PropTypes.number.isRequired,
    debounce: PropTypes.number,
    rootTag: PropTypes.string,
    scrollCallback: PropTypes.func,
    scrollContainer: PropTypes.string.isRequired,
    spacerContents: PropTypes.any,
    spacerTag: PropTypes.string,
  };

  static defaultProps = {
    buildChild: null,
    childCount: null,
    childHeight: null,
    debounce: 5,
    rootTag: 'div',
    scrollCallback: () => {},
    scrollContainer: null,
    spacerContents: '',
    spacerTag: 'div',
  };

  constructor(props) {
    super(props);

    this.container = document.querySelector(this.props.scrollContainer);

    // Determine how many children will fit in the container
    // Add some extra so that a row is visible as the user scrolls in either direction
    this.childrenPerScreen =
      Math.ceil(this.container.clientHeight / this.props.childHeight) + 2;

    this.state = {
      startIndex: 0,
      endIndex: this.childrenPerScreen,
    };
  }

  componentDidMount() {
    const onScroll = Underscore.debounce(() => {
      const {startIndex, endIndex} = this.getStartEndIndexes(
        this.container.scrollTop,
      );

      this.setState({
        startIndex,
        endIndex,
      });

      this.props.scrollCallback(this.container.scrollTop);
    }, this.props.debounce);

    this.container.addEventListener('scroll', onScroll);
  }

  componentDidUpdate() {
    this.props.scrollCallback(this.container.scrollTop);
  }

  // StartIndex and endIndex are the first and last indexes
  // of the children that will be rendered.
  getStartEndIndexes(scroll) {
    const {childrenPerScreen} = this;
    const {childCount, childHeight} = this.props;

    // Start index is how far the user has scrolled past the top of the box, divided by the child height
    const startIndex = Math.floor(scroll / childHeight);

    // For end index, add the height of the window to the effective scroll, then divide by child height
    let endIndex = startIndex + childrenPerScreen;

    // This stops a bug where some browsers seemingly permit excessive scrollTop
    if (endIndex > childCount) {
      endIndex = childCount;
    }

    return {startIndex, endIndex};
  }

  /* Makes a dummy table row of a certain height */
  makeDummy(height, key) {
    const display = height > 0 ? 'table-row' : 'none';

    const props = {
      className: 'FastScrollbox-dummy',
      key,
      style: {
        padding: 0,
        margin: 0,
        border: 'none',
        height,
        display,
      },
    };

    return React.createElement(
      this.props.spacerTag,
      props,
      this.props.spacerContents,
    );
  }

  // Core logic function. Given a child generating function, and the total
  // number of expected children, it will generate the right number of
  // children, padded by dummy elements of the proper height
  trimAndPadChildren() {
    const {childCount} = this.props;
    const {startIndex, endIndex} = this.state;

    const children = Underscore
      // Slice out a set of children for the relevant indices
      .range(startIndex, endIndex)
      // Use the passed function to generate the row
      .map(this.props.buildChild)
      .map((row, index) => {
        // If that function returns a falsy value, use the placeholder row
        // The placeholder allows the UI to look reasonable while page data
        // Is being fetched
        if (!row) {
          const placeholderRow = this.makeDummy(
            this.props.childHeight,
            `placeholder-row-${index}`,
          );
          return placeholderRow;
        }

        return row;
      });

    // Calculate the required heights of the dummy elements
    const dummyTopHeight = startIndex * this.props.childHeight;
    const dummyBottomHeight = (childCount - endIndex) * this.props.childHeight;

    // Return a flattened array of children sandwiched with dummy elements
    return [
      this.makeDummy(dummyTopHeight, 'dummy-top'),
      ...children,
      this.makeDummy(dummyBottomHeight, 'dummy-bottom'),
    ];
  }

  render() {
    const props = {
      className: 'FastScrollbox',
      ref: (component) => (this._box = component),
    };

    const children = this.trimAndPadChildren();

    return React.createElement(this.props.rootTag, props, children);
  }
}
